import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { CompetitorSectionKey } from "./competitor-plan-constants"
import type {
  FieldCitation,
  FieldUpdatePayload,
  PlanGenerateInput,
  SegmentReport,
} from "../../types"
import type { CompanyDirectoryEntry } from "../../utils/segment-connections"
import type { PlanSectionView, PlanViewMode } from "../plan/plan-utils"

import { Skeleton } from "@/components/ui/skeleton"

import { COMPETITOR_SECTIONS } from "./competitor-plan-constants"
import { CompetitorSectionBody } from "./competitor-section-body"
import { useCompetitorPlan } from "../../hooks/use-competitor-plan"
import { usePlanLibraryDocs } from "../../hooks/use-plan-library-docs"
import { COMPANY_NAME } from "../../utils/branding"
import { GenerateModal } from "../plan/generate-modal"
import { PlanEmptyState, ThinEvidenceBanner } from "../plan/plan-callouts"
import { PlanCanvasView } from "../plan/plan-canvas-view"
import { PlanDocumentView } from "../plan/plan-document-view"
import { PlanShell } from "../plan/plan-shell"
import {
  competitorLabelledFields,
  dataGapLabels,
  PLAN_VIEW_MODE_STORAGE_KEY,
  planIsGenerated,
  planIsMostlyNs,
} from "../plan/plan-utils"
import { UpdateReviewModal } from "../plan/update-review-modal"
import { RelatedCompaniesRow } from "../related-companies-row"

function readInitialMode(): PlanViewMode {
  const stored = sessionStorage.getItem(PLAN_VIEW_MODE_STORAGE_KEY)
  return stored === "canvas" ? "canvas" : "document"
}

const SECTION_VIEWS: PlanSectionView[] = COMPETITOR_SECTIONS.map((def) => ({
  no: def.no,
  title: def.title,
  fieldKey: def.fieldKey,
  updatable: def.updatable,
  colSpan: def.canvas.colSpan,
  rowSpan: def.canvas.rowSpan,
}))

const updateTestId = (fieldKey: string) => `sa-plan-update-${fieldKey}`

export function CompetitorPlanView({
  segmentId,
  companyId,
  companyName,
  segmentName,
  segmentReport = null,
  canEdit,
  directory,
  onBack,
  onOpenCompany,
}: {
  segmentId: string
  companyId: string
  companyName: string
  segmentName: string
  /** Segment report evidence — feeds GenerateModal source selection when present. */
  segmentReport?: SegmentReport | null
  canEdit: boolean
  /** Portfolio company directory — feeds the "Related" lateral-nav row. */
  directory?: CompanyDirectoryEntry[]
  onBack: () => void
  /** Drill into a related company (omit to hide the Related row). */
  onOpenCompany?: (name: string, segmentId: string) => void
}) {
  const { t } = useTranslation()
  const { planQuery, generateMutation, requestUpdate, patchField, saveManual } = useCompetitorPlan(
    segmentId,
    companyId
  )
  const libraryDocs = usePlanLibraryDocs(companyId, segmentReport, canEdit)
  const [mode, setMode] = useState<PlanViewMode>(readInitialMode)
  const [generateOpen, setGenerateOpen] = useState(false)
  const [review, setReview] = useState<{ fieldKey: string; title: string } | null>(null)
  const [justGenerated, setJustGenerated] = useState(false)

  const plan = planQuery.data ?? null

  function changeMode(next: PlanViewMode) {
    setMode(next)
    sessionStorage.setItem(PLAN_VIEW_MODE_STORAGE_KEY, next)
  }

  function handleGenerate(input: PlanGenerateInput) {
    generateMutation.mutate(input, {
      onSuccess: () => {
        setGenerateOpen(false)
        setJustGenerated(true)
      },
      onError: () => toast.error(t("segmentAnalysis.plan.generateFailed")),
    })
  }

  function openUpdate(fieldKey: string, title: string) {
    setReview({ fieldKey, title })
    requestUpdate.mutate(fieldKey, {
      onError: () => toast.error(t("segmentAnalysis.plan.updateFailed")),
    })
  }

  function acceptSuggestion(payload: FieldUpdatePayload) {
    if (!review) return
    patchField.mutate(
      { fieldKey: review.fieldKey, payload },
      {
        // Accepting a field is the "review" this banner asked for — once the
        // user has acted on at least one field, the fresh-draft framing is
        // stale even though nothing in the plan data marks fields as reviewed.
        onSuccess: () => {
          setReview(null)
          setJustGenerated(false)
        },
        onError: () => toast.error(t("segmentAnalysis.plan.saveFailed")),
      }
    )
  }

  function saveManualSection(payload: FieldUpdatePayload) {
    saveManual.mutate(payload, {
      onSuccess: () => toast.success(t("segmentAnalysis.manualSection.saved")),
      onError: () => toast.error(t("segmentAnalysis.plan.saveFailed")),
    })
  }

  const backLabel = t("segmentAnalysis.plan.backToCompetitors", { segment: segmentName })
  const generated = plan ? planIsGenerated(plan) : false
  const labelledFields = useMemo(() => (plan ? competitorLabelledFields(plan) : []), [plan])
  const gaps = useMemo(() => dataGapLabels(labelledFields), [labelledFields])
  const mostlyNs = generated && planIsMostlyNs(labelledFields)
  const freshDraft = justGenerated || Boolean(plan?.meta.isFreshDraft)

  const suggestion =
    requestUpdate.data && review && requestUpdate.data.fieldKey === review.fieldKey
      ? requestUpdate.data
      : null

  const shellTitle = plan?.companyName ?? companyName
  const resolvedSegmentName = plan?.segmentName ?? segmentName

  const metaSlot = (
    <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      <span>
        <span className="font-semibold">{t("segmentAnalysis.plan.segment")}:</span>{" "}
        {resolvedSegmentName}
      </span>
      <span className="bg-secondary/40 text-foreground/80 rounded-md px-2 py-0.5 font-semibold">
        {t("segmentAnalysis.plan.benchmarkedVsCompany", { organization: COMPANY_NAME })}
      </span>
    </div>
  )

  function renderBody(fieldKey: string, onCitationClick?: (citation: FieldCitation) => void) {
    if (!plan) return null
    return (
      <CompetitorSectionBody
        canEdit={canEdit}
        isSavingManual={saveManual.isPending}
        plan={plan}
        sectionKey={fieldKey as CompetitorSectionKey}
        sources={plan.sources ?? []}
        onCitationClick={onCitationClick}
        onSaveManual={saveManualSection}
      />
    )
  }

  function renderPlanContent() {
    if (!generated) {
      return (
        <PlanEmptyState
          canEdit={canEdit}
          isGenerating={generateMutation.isPending}
          kind="competitor"
          onGenerate={() => setGenerateOpen(true)}
        />
      )
    }
    const planView =
      mode === "document" ? (
        <PlanDocumentView
          canEdit={canEdit}
          dataGaps={gaps}
          freshDraft={freshDraft}
          planKind="competitor"
          renderBody={renderBody}
          sections={SECTION_VIEWS}
          sources={plan?.sources ?? []}
          updateTestId={updateTestId}
          onUpdate={openUpdate}
        />
      ) : (
        <PlanCanvasView
          canEdit={canEdit}
          dataGaps={gaps}
          freshDraft={freshDraft}
          renderBody={(fieldKey) => renderBody(fieldKey)}
          sections={SECTION_VIEWS}
          updateTestId={updateTestId}
          onUpdate={openUpdate}
        />
      )
    return (
      <div className="space-y-4">
        {mostlyNs ? (
          <ThinEvidenceBanner
            canEdit={canEdit}
            isGenerating={generateMutation.isPending}
            onRegenerate={() => setGenerateOpen(true)}
          />
        ) : null}
        {planView}
      </div>
    )
  }

  if (planQuery.isPending) {
    return (
      <div className="space-y-4" data-testid="sa-competitor-plan">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  return (
    <div data-testid="sa-competitor-plan">
      <PlanShell
        backLabel={backLabel}
        canEdit={canEdit}
        isGenerating={generateMutation.isPending}
        kindLabel={t("segmentAnalysis.plan.competitorKind")}
        metaSlot={generated ? metaSlot : undefined}
        mode={mode}
        title={shellTitle}
        onBack={onBack}
        onModeChange={changeMode}
        onRegenerate={generated ? () => setGenerateOpen(true) : undefined}
      >
        <div className="space-y-4">
          <RelatedCompaniesRow
            companyName={shellTitle}
            directory={directory}
            preferSegmentId={segmentId}
            onOpenCompany={onOpenCompany}
          />
          {renderPlanContent()}
        </div>
      </PlanShell>

      <GenerateModal
        companyName={shellTitle}
        isGenerating={generateMutation.isPending}
        kind="competitor"
        libraryDocs={libraryDocs}
        open={generateOpen}
        onGenerate={handleGenerate}
        onOpenChange={setGenerateOpen}
      />
      <UpdateReviewModal
        current={suggestion?.current ?? null}
        isLoading={requestUpdate.isPending}
        isSaving={patchField.isPending}
        open={Boolean(review)}
        suggestion={suggestion?.suggestion ?? null}
        title={review?.title ?? ""}
        renderSection={(section) =>
          plan && review ? (
            <CompetitorSectionBody
              canEdit={false}
              isSavingManual={false}
              plan={{ ...plan, sections: { ...plan.sections, [review.fieldKey]: section } }}
              sectionKey={review.fieldKey as CompetitorSectionKey}
              sources={plan.sources ?? []}
              onSaveManual={() => undefined}
            />
          ) : null
        }
        onAccept={acceptSuggestion}
        onOpenChange={(open) => !open && setReview(null)}
      />
    </div>
  )
}
